import { Add, Close } from "@mui/icons-material"
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import { DataNode, Examination, File, NodeDataType } from "graphql/types"
import { enqueueSnackbar } from "notistack"
import { useState } from "react"
import { getPDFUrl } from "store/pdfService"
import { addToCurrentChapter } from "utils/addToReport"
import { createCaption } from "utils/createCaption"
import { escapeMarkdown } from "utils/escapeMarkdown"
import { getImageURL } from "utils/getFileURL"
import { v4 } from "uuid"

type FileBoxProps = {
  doc: File & { setupNote?: string }
  inTestReportUI: boolean
  examination: Examination
}

export const FileBox = (props: FileBoxProps) => {
  const { doc, inTestReportUI, examination } = props

  const [selectedDocument, setSelectedDocument] = useState<File | null>(null)
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false)

  const getThumbnailSrc = (doc: File) => {
    if (doc.type === "IMAGE") {
      return getImageURL(doc.id)
    }
    // For non-image files, return a placeholder or document icon
    return undefined
  }

  const createDataNodeFromFile = (file: File | any): DataNode => {
    // Extract the base File object from extended objects
    const baseFile: File = {
      id: file.id,
      name: file.name,
      type: file.type,
      fileURL: file.fileURL,
      filename: file.filename,
      createdAt: file.createdAt,
      createdBy: file.createdBy,
      description: file.description,
    }

    if (file.type === "IMAGE") {
      // Create an image DataNode
      return {
        id: v4(),
        dataType: NodeDataType.IMAGE,
        image: [baseFile.id],
        imageCaption: [createCaption(baseFile)],
        imageNumber: [1],
        text: "",
        editable: true,
        deletable: true,
        tableBody: [],
        tableCaption: "",
        tableHeader: [],
        tableNumber: 0,
        isAutoGenerated: false,
        sourceExaminationId: examination.id,
      }
    } else {
      // Create a text DataNode with the file name (escaped to prevent Markdown formatting)
      return {
        id: v4(),
        dataType: NodeDataType.TEXT,
        image: [],
        imageCaption: [],
        imageNumber: [],
        text: escapeMarkdown(file.name),
        editable: true,
        deletable: true,
        tableBody: [],
        tableCaption: "",
        tableHeader: [],
        tableNumber: 0,
        isAutoGenerated: false,
        sourceExaminationId: examination.id,
      }
    }
  }

  const handleAddToReport = (file: File | any) => {
    const dataNode = createDataNodeFromFile(file)

    // Try to add to current chapter first (if in test report UI)
    if (inTestReportUI) {
      const success = addToCurrentChapter(dataNode)
      if (success) {
        enqueueSnackbar(
          file.type === "IMAGE"
            ? `Bild "${file.name}" wurde zum Report hinzugefügt`
            : `Text "${file.name}" wurde zum Report hinzugefügt`,
          { variant: "success" }
        )
        return
      } else {
        enqueueSnackbar(
          "Kein aktives Kapitel ausgewählt. Bitte wählen Sie ein Kapitel im Report aus.",
          { variant: "warning" }
        )
        return
      }
    }
  }

  const handleDocumentClick = async (document: File) => {
    try {
      const url = await getPDFUrl(document.id)
      const newDocument = {
        ...document,
        fileURL: url,
      }
      setSelectedDocument(newDocument)
      setDocumentDialogOpen(true)
    } catch (error) {
      console.error("Error opening PDF:", error)
      enqueueSnackbar(`Fehler beim Öffnen des Dokuments: ${error}`, { variant: "error" })
    }
  }

  const handleCloseDocumentDialog = () => {
    setDocumentDialogOpen(false)
    setSelectedDocument(null)
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        transition: "all 0.2s",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: 1,
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: 80,
          mb: 1,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        onClick={() => handleDocumentClick(doc)}
      >
        {getThumbnailSrc(doc) ? (
          <img
            src={getThumbnailSrc(doc)}
            alt={doc.name}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Typography variant="h4" color="text.secondary">
            📄
          </Typography>
        )}
      </Box>
      <Typography variant="caption" textAlign="center" sx={{ fontWeight: 600 }}>
        {doc.name}
      </Typography>
      {inTestReportUI && (
        <IconButton onClick={() => handleAddToReport(doc)}>
          <Add />
        </IconButton>
      )}
      {doc.setupNote && (
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {doc.setupNote}
        </Typography>
      )}

      {/* Document Preview Dialog */}
      <Dialog open={documentDialogOpen} onClose={handleCloseDocumentDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">{selectedDocument?.name || "Document"}</Typography>
            <IconButton onClick={handleCloseDocumentDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              {selectedDocument.type === "IMAGE" ? (
                <img
                  src={getImageURL(selectedDocument.id)}
                  alt={selectedDocument.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "70vh",
                    objectFit: "contain",
                  }}
                />
              ) : selectedDocument.type === "PDF" ? (
                <Box sx={{ width: "100%", height: "70vh" }}>
                  <embed
                    src={selectedDocument.fileURL}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                  />
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="h2" color="text.secondary" sx={{ mb: 2 }}>
                    📄
                  </Typography>
                  <Typography variant="body1">{selectedDocument.name}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
